import { useState } from 'react';
import { useNavigate } from 'react-router';
import imgLogowithlcon1 from '../../assets/logo-cherry-color.webp';
import { SecondaryButton } from './SecondaryButton';
import { ConfirmModal } from './ConfirmModal';
import { clearAuth } from '../../lib/auth';
import { clearCustomerAuth } from '../../lib/customer-auth';
import { logout as proLogoutApi } from '../../lib/professional-api';
import { logout as customerLogoutApi } from '../../lib/customer-api';
import { useAuth } from '../../lib/use-auth';
import { useCustomerAuth } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';

interface SignUpHeaderProps {
  showBackButton?: boolean;
  mode?: 'signin' | 'signup';
  isProfessional?: boolean;
  /** Opt-in role/mode switch buttons in the top-right. Off by default:
   *  the auth pages now use on-page Customer/Professional tabs and the
   *  in-form "Create one / Already have an account" links, so the header
   *  switch is redundant. The Logout button (shown when the visitor is
   *  already signed in, e.g. mid-onboarding) is independent of this flag
   *  and always renders. */
  showSwitchButton?: boolean;
}

export function SignUpHeader({
  showBackButton = false,
  mode = 'signin',
  isProfessional = false,
  showSwitchButton = false
}: SignUpHeaderProps) {
  const navigate = useNavigate();
  const { isAuthenticated: proAuthed } = useAuth();
  const { isAuthenticated: customerAuthed } = useCustomerAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // The toggle only makes sense for visitors who haven't authed yet.
  // Once a pro/customer is signed in (e.g. mid-onboarding on
  // verify-identity), swap it for a Logout so they can actually exit
  // the session instead of staring at a button that no-ops.
  const isAuthedForRole = isProfessional ? proAuthed : customerAuthed;

  const handleNavigation = () => {
    // From inside an auth flow the role is already known, so route
    // straight to the role-specific page instead of bouncing the user
    // back through the chooser at /signup or /signin.
    if (isProfessional) {
      navigate(mode === 'signin' ? '/professional/signin' : '/professional/signup');
    } else {
      navigate(mode === 'signin' ? '/signin/customer' : '/signup/customer');
    }
  };

  const handleSwitchRole = () => {
    // Switch between customer and professional while keeping the same mode
    if (isProfessional) {
      navigate(mode === 'signin' ? '/signin/customer' : '/signup/customer');
    } else {
      navigate(mode === 'signin' ? '/professional/signin' : '/professional/signup');
    }
  };

  const handleConfirmLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      if (isProfessional) {
        await proLogoutApi();
      } else {
        await customerLogoutApi();
      }
    } catch {
      // Network/4xx — still clear local auth so the user isn't stuck.
    } finally {
      if (isProfessional) clearAuth();
      else clearCustomerAuth();
      setLoggingOut(false);
      setConfirmOpen(false);
      toast.success('Logged out.');
      navigate('/', { replace: true });
    }
  };

  return (
    <header className="px-6 py-6 flex justify-between items-center max-w-[1200px] mx-auto w-full">
      <div className="w-[120px] cursor-pointer" onClick={() => navigate('/')}>
        <img src={imgLogowithlcon1} alt="Cherry Picked" className="w-full h-full object-contain" />
      </div>
      <div className="flex items-center gap-2">
        {showBackButton && (
          <SecondaryButton
            icon={<span className="material-icons text-[18px]">arrow_back</span>}
            onClick={() => navigate(-1)}
          >
            Back
          </SecondaryButton>
        )}
        {isAuthedForRole ? (
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={loggingOut}
            className="px-6 py-2 bg-[#f4f4f4] text-[#3a3335] font-['DM_Sans',sans-serif] font-bold text-sm rounded-lg hover:bg-[#eee] transition-colors h-[44px] disabled:opacity-60"
          >
            {loggingOut ? 'Logging out…' : 'Logout'}
          </button>
        ) : showSwitchButton ? (
          <>
            <button
              onClick={handleSwitchRole}
              className="px-6 py-2 bg-white text-[#3a3335] font-['DM_Sans',sans-serif] font-bold text-sm rounded-lg border border-[#eee] hover:border-[#8e2a2f] hover:text-[#8e2a2f] transition-colors h-[44px]"
            >
              {isProfessional ? 'Customer' : 'Professional'}
            </button>
            <button
              onClick={handleNavigation}
              className="px-6 py-2 bg-[#f4f4f4] text-[#3a3335] font-['DM_Sans',sans-serif] font-bold text-sm rounded-lg hover:bg-[#eee] transition-colors h-[44px]"
            >
              {mode === 'signin' ? 'Sign In' : 'Sign Up'}
            </button>
          </>
        ) : null}
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmLogout}
        title="Log out?"
        message="You'll need to sign in again to come back to your account."
        confirmLabel="Log out"
        cancelLabel="Stay signed in"
        confirming={loggingOut}
      />
    </header>
  );
}
